import { portalConfig } from './portal-config.js';

export class UnauthorizedError extends Error {
  constructor() {
    super('unauthorized');
    this.name = 'UnauthorizedError';
  }
}

// Carries the HTTP status so retry logic can distinguish transient server
// errors (502/503/504 — worth retrying) from definitive ones.
export class HttpError extends Error {
  constructor(public readonly status: number) {
    super(`HTTP ${status}`);
    this.name = 'HttpError';
  }
}

// Tolerate a little clock skew so a token that's *about* to expire is treated
// the same as one that just has — both get rejected by the authorizer anyway.
const CLOCK_SKEW_MS = 30_000;

function decodeJwtExpMs(token: string): number | null {
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    let b64 = parts[1]!.replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4) b64 += '=';
    const { exp } = JSON.parse(atob(b64)) as { exp?: number };
    return typeof exp === 'number' ? exp * 1000 : null;
  } catch {
    return null;
  }
}

export function isTokenExpired(token: string, skewMs = CLOCK_SKEW_MS): boolean {
  const expMs = decodeJwtExpMs(token);
  if (expMs === null) return false;
  return Date.now() >= expMs - skewMs;
}

export async function apiFetch(path: string, idToken: string, init: RequestInit = {}): Promise<Response> {
  const res = await fetch(`${portalConfig.apiBase}${path}`, {
    ...init,
    headers: { ...(init.headers ?? {}), Authorization: `Bearer ${idToken}` }
  });
  // A missing Authorization header makes API Gateway return 401 before the
  // authorizer even runs. But the common case — a *present but expired*
  // id_token — is rejected by the OIDC Lambda authorizer, which (as an HTTP
  // API SIMPLE authorizer) surfaces as 403, not 401. Treat that 403 as an
  // auth-expiry signal so the same refresh path recovers it. A 403 with a
  // still-valid token is a genuine authorization denial (wrong hosted-domain
  // or insufficient role) and must surface — never refresh, never loop.
  if (res.status === 401) {
    throw new UnauthorizedError();
  }
  if (res.status === 403 && isTokenExpired(idToken)) {
    throw new UnauthorizedError();
  }
  return res;
}

export async function apiJson<T>(path: string, idToken: string): Promise<T> {
  const res = await apiFetch(path, idToken);
  if (!res.ok) throw new HttpError(res.status);
  return res.json() as Promise<T>;
}

export class ApiError extends Error {
  constructor(public status: number, public body: string) {
    super(`HTTP ${status}: ${body}`);
    this.name = 'ApiError';
  }
}

export async function apiSendJson<T>(
  path: string,
  method: 'POST' | 'PUT' | 'DELETE' | 'PATCH',
  idToken: string,
  body?: unknown
): Promise<T> {
  const res = await apiFetch(path, idToken, {
    method,
    headers: { 'content-type': 'application/json' },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const text = await res.text();
    throw new ApiError(res.status, text);
  }
  // 204 No Content + 205 Reset Content always have empty bodies. Some
  // servers also send 200 with empty body or no content-length under
  // chunked encoding — guard those by inspecting content-type / falling
  // back to text() if json parsing would otherwise throw.
  if (res.status === 204 || res.status === 205) return {} as T;
  const contentLength = res.headers.get('content-length');
  if (contentLength === '0') return {} as T;
  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.includes('json')) {
    const text = await res.text();
    if (text.length === 0) return {} as T;
    return text as unknown as T;
  }
  return res.json() as Promise<T>;
}
